"use client";
import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import ReactDOM from "react-dom";
import Cookies from "js-cookie";
import { validatePhoneNumber, sanitizePhoneInput, validateName } from "@/app/utilities/formValidation";
import { createDealerLead, submitSupportLead } from "@/app/utilities/apis";
import { LeadFormData } from "@/app/utilities/interfaces";
import { getLocationString } from "@/app/utilities/locationCookie";

import ImageWrapper from "../ImageWrapper";
import CustomInput from "../CustomInput";
import LocationMasterSearchInput from "../LocationMasterSearchInput";
import { mapLocationToLeadFields } from "@/app/utilities/locationUtils";
import ThanksPopup from '@/component/ThanksPopup';
import { acquireModalSession, releaseModalSession } from "@/lib/client/modalSession";
import { consumeMobileLeadKeyboardRequest, focusFirstEmptyLeadFieldOnMobile } from "./LeadForm";
import { useLeadConsent } from "@/utils/hooks/useLeadConsent";
import LeadConsent from "./LeadConsent";

import { THANKS_POPUP_SESSION_ID } from "@/app/utilities/constants";

interface DealerLeadModalProps {
    showModal: boolean;
    onClose: () => void;
    onSubmit: () => void;
    onError: (message: string) => void;
    dealer_id?: string;
    selectedModel?: {
        _id: string;
        name: string;
        slug: string;
        brand_name: string;
        brand_slug: string;
    } | null;
}


export default function DealerLeadModal({
    showModal,
    onClose,
    onSubmit,
    onError,
    dealer_id,
    selectedModel,
}: DealerLeadModalProps) {
    const [isMounted, setIsMounted] = useState(false);
    const [form, setForm] = useState<LeadFormData>({
        name: "",
        phone: "",
        location: "",
    });

    const [errors, setErrors] = useState({
        name: null as string | null,
        phone: null as string | null,
        location: null as string | null,
    });

    const [isLoading, setIsLoading] = useState(false);
    // Mounted while closed — fetch the consent config only once opened.
    const consent = useLeadConsent(showModal);
    const [showThanks, setShowThanks] = useState(false);
    const [lastLeadData, setLastLeadData] = useState<any>(null);

    // Hydration safety + prefill from cookies
    useEffect(() => {
        setIsMounted(true);

        if (!showModal) return;

        const userCookie = Cookies.get("user");

        let name = "";
        let phone = "";

        if (userCookie) {
            try {
                const user = JSON.parse(decodeURIComponent(userCookie));
                name = user?.user?.name || "";
                phone = user?.user?.mobile || "";
            } catch { }
        }

        setForm({
            name,
            phone: sanitizePhoneInput(phone).slice(0, 10),
            location: "",
            locationData: {},
            utm_campaign: Cookies.get("utm_campaign"),
            utm_medium: Cookies.get("utm_medium"),
            utm_term: Cookies.get("utm_term"),
            utm_source: Cookies.get("utm_source"),
            utm_content: Cookies.get("utm_content"),
        });
    }, [showModal]);

    const leadFormAutofocusRef = useRef<HTMLDivElement>(null);
    const didAutofocusRef = useRef(false);

    useLayoutEffect(() => {
        if (!showModal) {
            didAutofocusRef.current = false;
            return;
        }
        if (didAutofocusRef.current) return;
        didAutofocusRef.current = true;
        const fromUserGesture = consumeMobileLeadKeyboardRequest();
        const runFocus = () => focusFirstEmptyLeadFieldOnMobile(leadFormAutofocusRef.current);
        if (fromUserGesture) {
            runFocus();
            requestAnimationFrame(runFocus);
            window.setTimeout(runFocus, 50);
            return;
        }
        const timer = window.setTimeout(runFocus, 220);
        return () => window.clearTimeout(timer);
    }, [showModal]);

    // Handlers
    const updateForm = (field: keyof LeadFormData, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^A-Za-z\s]/g, "");
        updateForm("name", value);
        setErrors((prev) => ({ ...prev, name: validateName(value) }));
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = sanitizePhoneInput(e.target.value).slice(0, 10);
        updateForm("phone", value);
        setErrors((prev) => ({ ...prev, phone: validatePhoneNumber(value) }));
    };

    const handleLocationMasterChange = (value: string, selectedLocation?: any) => {
        const mapped = selectedLocation ? mapLocationToLeadFields(selectedLocation) : { locationData: {} };
        setForm((prev) => ({ ...prev, location: value, locationData: mapped.locationData || {} }));

        if (value.trim().length === 0) {
            setErrors((prev) => ({ ...prev, location: "Location is required" }));
        } else if (value.trim().length < 2) {
            setErrors((prev) => ({ ...prev, location: "Location must be at least 2 characters" }));
        } else {
            setErrors((prev) => ({ ...prev, location: null }));
        }
    };

    const handleSubmit = async () => {
        if (isLoading || !consent.canSubmit) return; // prevent duplicate submissions

        setIsLoading(true);

        if (!form.name || !form.phone || !form.location) {
            onError("Please fill in all fields.");
            setIsLoading(false);
            return;
        }

        if (errors.name || errors.phone || errors.location) {
            setIsLoading(false);
            return;
        }

        try {
            const { buildLeadPayload } = await import('@/app/utilities/locationUtils');
            const payload = buildLeadPayload(form, {dealerId: dealer_id, carId: selectedModel?._id});

            const res = await createDealerLead(payload);
            if (res.status !== "success") throw new Error("Submit failed");

            const leadData = {
                ...payload,
                brand: selectedModel?.brand_name,
                modelName: selectedModel?.name,
            };
            if (typeof window !== "undefined") {
                (window as any).__LAST_LEAD_DATA__ = leadData;
                if (selectedModel?._id) {
                    window.dispatchEvent(
                        new CustomEvent("lead-submitted", {
                            detail: {
                                carId: selectedModel._id,
                                carName: `${selectedModel.brand_name} ${selectedModel.name}`.trim(),
                            },
                        }),
                    );
                }
            }
            setLastLeadData(leadData);
            acquireModalSession(THANKS_POPUP_SESSION_ID);
            setShowThanks(true);

            // Reset
            setForm({ name: "", phone: "", location: "", locationData: {} });
            setErrors({ name: null, phone: null, location: null });
            onSubmit();
        } catch (err) {
            console.error(err);
            onError("Failed to submit. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    if (!isMounted || (!showModal && !showThanks)) return null;

    const hasError = !!errors.name || !!errors.phone || !!errors.location || !consent.canSubmit;

    const closeThanksPopup = () => {
        setShowThanks(false);
        releaseModalSession(THANKS_POPUP_SESSION_ID);
    };

    return ReactDOM.createPortal(
        <>
        {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100]">
            <div className="bg-white rounded-3xl w-[328px] md:w-[448px] max-w-[95vw]">
                <div className="p-5 md:p-7 relative" ref={leadFormAutofocusRef}>
                    {/* Header */}
                    <button
                        onClick={onClose}
                        className="absolute top-5 right-6 text-gray-500 hover:text-gray-800"
                        aria-label="Close"
                    >
                        <ImageWrapper
                            src="/popup-close.svg"
                            alt="Close"
                            width={18}
                            height={18}
                            cdnBaseUrl={process.env.NEXT_PUBLIC_CDN_STATIC_URL}
                        />
                    </button>

                    <h2 className="text-xl md:text-[19px] font-semibold  text-[#0F172A] mb-7 pr-10 leading-tight">
                        {/* Get Best Offers On{" "}
                        <span className="font-semibold tracking-[-0.75px] text-[#008C8B]">
                            {selectedModel?.brand_name} {selectedModel?.name}
                        </span> */}
                        Get dealer contact
                        <span className="block text-[13px] font-medium tracking-[-0.75px] text-[#64748B] py-1">
                            Confirm your details to view the number.
                        </span>
                    </h2>

                    {/* Form */}
                    <div className="space-y-4">                        

                        <CustomInput
                            value={form.name}
                            placeholder="Enter name"
                            leftIcon={
                                <ImageWrapper
                                    src="/user.svg"
                                    alt="User"
                                    width={18}
                                    height={18}
                                    cdnBaseUrl={process.env.NEXT_PUBLIC_CDN_STATIC_URL}
                                />
                            }
                            onChange={handleNameChange}
                            error={errors.name ?? undefined}
                            className="bg-gray-50"
                        />

                        <CustomInput
                            type="tel"
                            value={form.phone}
                            placeholder="Enter mobile number"
                            maxLength={10}
                            leftIcon={
                                <ImageWrapper
                                    src="/phone.svg"
                                    alt="Phone"
                                    width={18}
                                    height={18}
                                    cdnBaseUrl={process.env.NEXT_PUBLIC_CDN_STATIC_URL}
                                />
                            }
                            onChange={handlePhoneChange}
                            error={errors.phone ?? undefined}
                            className="bg-gray-50"
                        />

                        <label className="block mb-4 w-full ">
                            <LocationMasterSearchInput value={form.location || ""} onChange={handleLocationMasterChange} />
                        </label>
                    </div>

                    <LeadConsent
                        consent={consent}
                        className="mb-3"
                    />

                    <button
                        onClick={handleSubmit}
                        disabled={hasError || isLoading}
                        data-testid="dealer-lead-submit"
                        className={`w-full py-3.5 rounded-lg font-medium text-white ${hasError || isLoading ? "bg-teal-400 cursor-not-allowed" : "bg-teal-600 hover:bg-teal-700"} transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer`}
                    >
                        {isLoading ? "Submitting..." : "Submit"}
                    </button>
                </div>
            </div>
        </div>
        )}
        <ThanksPopup
            isOpen={showThanks}
            onClose={closeThanksPopup}
            modelId={selectedModel?._id ?? null}
            leadData={lastLeadData}
            model={selectedModel ?? undefined}
        />
        </>,
        document.body
    );
}
